// Admin API Service
// When running behind Nginx, API is on same origin at /api
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('adminToken');
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  });
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'API Error');
  }
  
  return data;
};

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const response = await apiCall('/api/auth.php?action=login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    
    if (response.success && response.data?.token) {
      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminUser', JSON.stringify(response.data.user));
    }
    
    return response;
  },
  
  logout: async () => {
    try {
      await apiCall('/api/auth.php?action=logout', {
        method: 'GET',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
    }
  },
  
  getCurrentUser: async () => {
    return await apiCall('/api/auth.php?action=me', {
      method: 'GET',
    });
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('adminToken');
  },
  
  getStoredUser: () => {
    const user = localStorage.getItem('adminUser');
    return user ? JSON.parse(user) : null;
  },
};

// Registrations API
export const registrationsAPI = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.sort) queryParams.append('sort', params.sort);
    if (params.order) queryParams.append('order', params.order);
    
    const queryString = queryParams.toString();
    const endpoint = `/api/registrations.php${queryString ? `?${queryString}` : ''}`;
    
    return await apiCall(endpoint, { method: 'GET' });
  },
  
  getById: async (id) => {
    return await apiCall(`/api/registrations.php?id=${id}`, {
      method: 'GET',
    });
  },
  
  update: async (id, data) => {
    return await apiCall(`/api/registrations.php?id=${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  
  delete: async (id) => {
    return await apiCall(`/api/registrations.php?id=${id}`, {
      method: 'DELETE',
    });
  },
  
  getStats: async () => {
    return await apiCall('/api/registrations.php?stats=true', {
      method: 'GET',
    });
  },
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    try {
      const response = await registrationsAPI.getAll({ limit: 1000 });
      const registrations = response.data?.registrations || [];
      const apiStats = response.data?.stats || {};
      
      const stats = {
        total: apiStats.total || registrations.length,
        new: apiStats.by_status?.new || registrations.filter(r => r.status === 'new').length,
        contacted: apiStats.by_status?.contacted || registrations.filter(r => r.status === 'contacted').length,
        in_progress: apiStats.by_status?.in_progress || registrations.filter(r => r.status === 'in_progress').length,
        completed: apiStats.by_status?.completed || registrations.filter(r => r.status === 'completed').length,
        cancelled: apiStats.by_status?.cancelled || registrations.filter(r => r.status === 'cancelled').length,
        recentRegistrations: registrations.slice(0, 5),
      };
      
      return { success: true, data: stats };
    } catch (error) {
      throw error;
    }
  },
};

const api = {
  auth: authAPI,
  registrations: registrationsAPI,
  dashboard: dashboardAPI,
};

export default api;